import { useSearch } from "@tanstack/react-router";

import type { Page } from "../../api";
import { Chip } from "../../chips";
import { OptionLink } from "../../links";
import { TabPage } from "../../PageFrame";
import { Legend, Swatch } from "../bars";
import { VBars, type BarPoint } from "../charts";
import type { PageProps } from "../index";
import { Footnote, money, PageHead } from "../kit";
import { ALL_ROWS, pageExtra } from "../params";

export interface CostRow {
  option_id: string;
  option_name: string;
  item_id: string;
  item_name: string;
  funnel: string;
  phase: string | null;
  cost_type: string;
  variant: string | null;
  cost_gbp: number | null;
  incoterm: string | null;
  quantity: number | null;
  moq: number | null;
  pricing_date: string | null;
  no_offer: boolean;
  item_average_gbp: number | null;
}

export interface CostModel {
  /** Options with a cost, cheapest first (C1's order). */
  points: BarPoint[];
  /** Options with no offer among the rows held. */
  none: CostRow[];
  /** Rows C1 holds beyond the ones fetched; they sort after the costed ones, so they have no offer either. */
  more: number;
  average: number | null;
  /** 2 decimals for per-unit costs, none for yearly totals. */
  dp: 0 | 2;
}

export function costModel(rows: CostRow[], total: number): CostModel {
  const costed = rows.filter((row) => row.cost_gbp != null);
  const highest = Math.max(0, ...costed.map((row) => row.cost_gbp ?? 0));
  return {
    points: costed.map((row) => ({
      // An ingredient option can carry one offer per variant; the variant tells its bars apart.
      label: row.variant ? `${row.option_name} · ${row.variant}` : row.option_name,
      value: row.cost_gbp,
      option: { optionId: row.option_id, itemId: row.item_id },
    })),
    none: rows.filter((row) => row.cost_gbp == null),
    more: Math.max(0, total - rows.length),
    average: rows[0]?.item_average_gbp ?? null,
    dp: highest < 20 ? 2 : 0,
  };
}

export const costFormat = (dp: 0 | 2) => (value: number) =>
  dp === 2 ? value.toFixed(2) : Math.round(value).toLocaleString("en-GB");

export function SourcingFunnelBody({ data }: { data: Page<CostRow> }) {
  const model = costModel(data.items, data.total);
  const average = money(model.average, model.dp);
  return (
    <>
      <Legend>
        <Swatch className="bg-ks-purple">GBP {data.items[0]?.cost_type}</Swatch>
        <span className="flex items-center gap-1.5 text-ks-grey-1">
          <i aria-hidden="true" className="inline-block w-4 border-t-2 border-dashed border-ks-ink" />
          Average {average}
        </span>
      </Legend>
      <VBars
        title="Cost per option"
        slot={60}
        rotate
        points={model.points}
        format={costFormat(model.dp)}
        from="sourcing_funnel"
        reference={
          model.average == null
            ? undefined
            : { value: model.average, label: `Average ${average}`, tone: "ink", dashed: true, width: 2 }
        }
      />
      {model.none.length > 0 ? (
        <Footnote>
          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            No offer yet:
            {model.none.map((row, index) => (
              <span key={`${row.option_id}:${row.variant ?? ""}`}>
                <OptionLink optionId={row.option_id} itemId={row.item_id} from="sourcing_funnel">
                  {row.option_name}
                </OptionLink>
                {index < model.none.length - 1 ? "," : null}
              </span>
            ))}
            {model.more > 0 ? <Chip tone="muted">and {model.more} more</Chip> : null}
          </span>
        </Footnote>
      ) : null}
    </>
  );
}

export function SourcingFunnelPage({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  return (
    <TabPage<Page<CostRow>>
      tab={tab}
      areaName={areaName}
      extra={{ ...pageExtra(tab.tabKey, search), limit: ALL_ROWS }}
      isEmpty={(data) => data.items.length === 0}
      head={(data) => {
        const first = data?.items[0];
        const model = data ? costModel(data.items, data.total) : null;
        return (
          <PageHead
            title={tab.label}
            sub={first ? `${first.item_name} · ${first.cost_type} in GBP · dashed line = item average` : undefined}
            kpi={model ? { label: "Average", value: money(model.average, model.dp) } : null}
          />
        );
      }}
    >
      {(data) => <SourcingFunnelBody data={data} />}
    </TabPage>
  );
}
